import { allMenuIds } from './menuConfig'

/**
 * One-click starting points for the two audiences this was built for.
 *
 * They fill the Role builder's menu picker; they are not a separate kind of thing. Whoever applies one
 * can tick a row on or off before saving, and the result is an ordinary role — which is also how a team
 * gets a third preset without anyone shipping code for it.
 */
interface MenuPreset {
    id: string
    label: string
    description: string
    menuIds: string[]
}

export const MENU_PRESETS: MenuPreset[] = [
    {
        id: 'box-truck-dispatcher',
        label: 'Box Truck Dispatcher',
        description: 'The working surfaces only — the board, the bids, the drivers on it, and how to reach them.',
        menuIds: ['box-truck-loadboard', 'box-truck-bidding', 'box-truck-my-drivers', 'box-truck-map', 'box-truck-mailing', 'box-truck-sms'],
    },
    {
        id: 'box-truck-hr',
        label: 'Box Truck HR',
        description: 'Profiles only — drivers, verifications, texting them, owners, assets and brokers.',
        menuIds: ['profiles-drivers', 'box-truck-driver-verifications', 'box-truck-driver-sms', 'profiles-contractors', 'profiles-assets', 'profiles-brokers'],
    },
]

/** A preset's rows that the menu still has. A row renamed or retired upstream is dropped rather than
 *  stored as an id nothing matches — the picker would show the preset as selecting something invisible. */
export const presetMenuIds = (preset: MenuPreset): string[] => {
    const known = new Set(allMenuIds())
    return preset.menuIds.filter((id) => known.has(id))
}

/**
 * What a Dispatch Manager sees when nobody has chosen for them: both presets at once.
 *
 * A manager runs a desk — they work the board AND the people on it — so either preset alone describes half
 * their job. This is the one place a menu selection has a non-empty default: everywhere else absent means
 * "no restriction", and here it means this. An owner who wants a manager to see the whole sidebar saves an
 * EMPTY selection, which is a different value than never having chosen.
 */
export const dispatchManagerDefaultMenuIds = (): string[] => [...new Set(MENU_PRESETS.flatMap((preset) => presetMenuIds(preset)))]
